import { useCallback, useId, useMemo, useRef, useState } from "react";
import { useFilteredOptions } from "../common/hooks/useFilteredOption";
import { useSelectKeyboard } from "../common/hooks/useSelectKeyboard";
import { LabeledTriggerValue } from "../common/LabeledTriggerValue";
import { CommonSelectProps, Option, SelectBase } from "../common/SelectBase";
import { SingleSelectOptions } from "./SingleSelectOptions";
import { SingleTriggerValue } from "./SingleTriggerValue";

export interface SingleSelectProps extends CommonSelectProps {
  /**
   *  Array of selectable item values
   */
  options: Option[];
  /**
   * Callback when the selected value changes
   */
  onChange: (value: string) => void;
  /**
   * Selected option
   */
  selectedOption?: Option;
  /**
   * Action icons rendered inside the trigger, between the value and the chevron.
   */
  triggerActions?: React.ReactNode;
  /**
   * Optional content rendered in the popover footer. May be a ReactNode or a
   * render function that receives a ``close`` callback — use the latter to
   * close the dropdown when a footer action (e.g. "New Version") is triggered.
   */
  footerContent?: React.ReactNode | ((close: () => void) => React.ReactNode);
  /**
   * When true, typing a printable key on the focused trigger opens the
   * popover and seeds the search with that key. Tab on the search input
   * commits the active match and closes the popover, so Tab naturally
   * advances focus to the next dropdown.
   */
  typeToSearch?: boolean;
  /**
   * When true, a clear (×) button appears next to the chevron when a value
   * is selected. Clicking it calls `onChange("")`. Defaults to `true` when
   * `label` is set (filter-bar usage) and `false` otherwise.
   */
  clearable?: boolean;
  /**
   * Called when the popover opens or closes — e.g. to lazily fetch options
   * on first open. Fires once per transition, whatever triggered it (click,
   * keyboard, outside dismiss).
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Opt-in creatable mode: when the search text matches no option, the list
   * offers that text as a new value. Needs the search input, so leave
   * `searchable` on. Off by default — the select stays a pure picker.
   */
  allowCreate?: boolean;
  /**
   * Called instead of `onChange` when the user picks the create row, with the
   * new value as typed (trimmed). This is how a consumer tells "created" from
   * "picked" — e.g. to warn that a new value behaves differently. Omit it and
   * created values arrive through `onChange` like any other.
   */
  onCreate?: (value: string) => void;
  /**
   * Label for the create row. Defaults to `Use "<typed text>"`.
   */
  formatCreateLabel?: (value: string) => string;
}

/**
 * Comparison form for "does the typed text already exist?" — case-folded and
 * whitespace-collapsed. Deliberately duplicated from `normalizeDimValue` in
 * `@appretio/cube` (which mirrors the backend rule): ui-kit must not depend on
 * a domain package.
 */
function normalizeForMatch(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function defaultCreateLabel(value: string): string {
  return `Use "${value}"`;
}

export function SingleSelect({
  options,
  selectedOption,
  onChange,
  size = "sm",
  triggerActions,
  footerContent,
  typeToSearch = false,
  clearable,
  filter,
  onOpenChange,
  allowCreate = false,
  onCreate,
  formatCreateLabel = defaultCreateLabel,
  ...rest
}: SingleSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const listId = useId();

  // Every open/close path funnels through changeOpen so onOpenChange fires
  // exactly once per transition. The ref (not the `open` closure) dedupes:
  // a trigger click reaches this twice in one tick — Radix's own
  // onOpenChange plus our onClick toggle — before state settles.
  const notifiedOpenRef = useRef(false);
  const changeOpen = useCallback(
    (next: boolean) => {
      if (notifiedOpenRef.current !== next) {
        notifiedOpenRef.current = next;
        onOpenChange?.(next);
      }
      setOpen(next);
    },
    [onOpenChange]
  );

  // Creatable mode filters on the trimmed query, so a trailing space can't
  // empty the list while the create row is suppressed as an exact match.
  const filteredOptions = useFilteredOptions(
    options,
    allowCreate ? search.trim() : search,
    filter
  );

  // The typed text as a selectable row — present only while it matches no
  // option, case and whitespace ignored.
  const createOption = useMemo<Option | undefined>(() => {
    const typed = search.trim();
    if (!allowCreate || typed === "") return undefined;

    const normalized = normalizeForMatch(typed);
    const exists = options.some(
      (option) =>
        normalizeForMatch(option.label) === normalized ||
        normalizeForMatch(option.value) === normalized
    );
    return exists
      ? undefined
      : { label: formatCreateLabel(typed), value: typed };
  }, [allowCreate, options, search, formatCreateLabel]);

  // One list, so the create row navigates and commits like any other row.
  const rows = useMemo(
    () => (createOption ? [...filteredOptions, createOption] : filteredOptions),
    [filteredOptions, createOption]
  );

  const emitChange = useCallback(
    (option: Option) => {
      // Created values reach the caller as typed (trimmed) — normalisation is
      // only the yardstick for "does this already exist?".
      if (option === createOption && onCreate) {
        onCreate(option.value);
        return;
      }
      onChange(option.value);
    },
    [onChange, onCreate, createOption]
  );

  const handleOptionSelect = useCallback(
    (option: Option) => {
      emitChange(option);
      changeOpen(false);
      setSearch("");
    },
    [emitChange, changeOpen]
  );

  function handleTogglePopover() {
    changeOpen(!open);
    setSearch("");
  }

  function scrollActiveIntoView(index: number) {
    const item = document
      .getElementById(listId)
      ?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    item?.scrollIntoView({ block: "nearest" });
  }

  // Shared keyboard model: Arrow/Home/End navigation, Enter/Space to select the
  // active row (closes for single-select), Tab to commit-and-close.
  const { activeIndex, activeOption, handleKeyDown } = useSelectKeyboard({
    options: rows,
    search,
    isOpen: open,
    typeToSearch,
    disabled: rest.loading,
    scrollToIndex: scrollActiveIntoView,
    onSelect: handleOptionSelect,
    onTab: emitChange,
    onClose: () => {
      changeOpen(false);
      setSearch("");
    }
  });

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (
      typeToSearch &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      setSearch(event.key);
      changeOpen(true);
    }
  }

  const resolvedFooter =
    typeof footerContent === "function"
      ? footerContent(() => changeOpen(false))
      : footerContent;

  const hasValue = !!selectedOption?.value;
  const isLabeled = rest.label !== undefined;
  const isClearable = clearable ?? isLabeled;
  // The create row's value is the typed text, which may contain whitespace —
  // it gets a fixed id rather than a value-derived one.
  const createOptionId = `${listId}-create`;
  const activeDescendantId = activeOption
    ? activeOption === createOption
      ? createOptionId
      : `${listId}-${activeOption.value}`
    : undefined;

  return (
    <SelectBase
      {...rest}
      size={size}
      isPopoverOpen={open}
      setIsPopoverOpen={changeOpen}
      handleTogglePopover={handleTogglePopover}
      triggerActions={triggerActions}
      shouldFilter={false}
      searchValue={search}
      onSearchValueChange={setSearch}
      onTriggerKeyDown={typeToSearch ? handleTriggerKeyDown : undefined}
      onContentKeyDownCapture={handleKeyDown}
      listId={listId}
      activeDescendantId={activeDescendantId}
      clearAll={isClearable && hasValue}
      clearAllSelection={() => onChange("")}
      triggerContent={
        isLabeled ? (
          <LabeledTriggerValue
            label={rest.label!}
            selectedValues={hasValue ? [selectedOption!.value] : []}
            options={options}
            placeholder={rest.placeholder}
            size={size}
          />
        ) : (
          <SingleTriggerValue
            value={
              selectedOption?.value !== "" ? (selectedOption?.label ?? "") : ""
            }
            placeholder={rest.placeholder}
          />
        )
      }
      popoverContent={
        <SingleSelectOptions
          options={filteredOptions}
          selectedOption={selectedOption}
          onOptionSelect={handleOptionSelect}
          size={size}
          activeIndex={activeIndex}
          listId={listId}
          createOption={createOption}
          createOptionId={createOptionId}
        />
      }
      footerContent={resolvedFooter}
    />
  );
}
